import { useEffect } from 'react';
import { takeUntil, withLatestFrom } from 'rxjs';
import { trpc } from '@client-trpc';
import { filterWidgetEvent } from '@client/foundation/shared/utils/filter-widget-event';
import { useWidget } from '@client/widgets/widget.hook';
import type { WidgetPayload } from '@client/widgets/widget.types';
import { useWindow } from '@client/window/window.hook';
import { useDraftState } from '@client/state/draft-form-state.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';
import { buildLayoutSnapshot } from './layout-snapshot.utils';
import { useCreateLayout } from './create-layout.hook';
import type { SaveTradingLayoutFormState } from './save-trading-layout-form';

export const useCreateLayoutForm = () => {
  const { openWidget$ } = useWidget();
  const { openWindow } = useWindow();
  const { state$ } = useDraftState();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();
  const { onCreateSubmit, onUpdateSubmit } = useCreateLayout();
  const trpcClient = trpc.useUtils().client;

  useEffect(() => {
    const subscription = openWidget$()
      .pipe(
        filterWidgetEvent<WidgetPayload, 'SaveTradingLayoutForm'>(
          'SaveTradingLayoutForm'
        ),
        withLatestFrom(state$),
        takeUntil(workspaceDestroy$)
      )
      .subscribe(async ([payload, draftState]) => {
        const formState = payload.formState ?? draftState;
        if (!formState) {
          console.error('No draft state found');
          return;
        }

        const snapshot = buildLayoutSnapshot(formState);

        const baseSubmitHandler = payload.layoutId
          ? onUpdateSubmit(payload.layoutId, snapshot)
          : onCreateSubmit(snapshot);

        const submitHandler = async (formState: SaveTradingLayoutFormState) => {
          const savedLayout = await baseSubmitHandler(formState);
          if (savedLayout) {
            // Bridge save modal and already-open TradingForm.
            // This avoids waiting for a separate "Apply" action from the grid.
            payload.onSaved?.(savedLayout);
          }
        };

        const layoutNotes = payload.layoutId
          ? (
              await trpcClient.tradingLayouts.layouts.query()
            ).find(({ id }) => id === payload.layoutId)?.notes
          : undefined;

        openWindow({
          type: 'SaveTradingLayoutForm',
          sizePreset: 'form',
          componentProps: {
            onSubmit: submitHandler,
            initialValues: payload.layoutName
              ? { name: payload.layoutName, notes: layoutNotes }
              : undefined,
            submitLabel: payload.layoutId ? 'Update' : undefined
          }
        });
      });

    return () => subscription.unsubscribe();
  }, [
    onCreateSubmit,
    onUpdateSubmit,
    openWidget$,
    openWindow,
    state$,
    trpcClient,
    workspaceDestroy$
  ]);
};
